import { useTeamByName } from '@/api/hooks/teams';
import { resolveCrumbLabel } from '@/components/breadcrumbs/resolveCrumbLabel';
import type { ReactElement } from 'react';

/**
 * Breadcrumb leaf for `/teams/:slug`. Resolves the team's display name from
 * the SAME `useTeamByName(slug)` cache entry the detail page populates —
 * React Query dedupes by query key, so this costs zero extra network. Falls
 * back to the raw slug while the fetch is in flight, if it failed, or if the
 * slug doesn't resolve — a failed fetch gets a `title` tooltip so it isn't
 * visually indistinguishable from "still loading" (the actual error is
 * already telemetered by the mutator; this is just an operator-facing hint).
 *
 * Previously read a page of `useListTeams({ pageSize: 100 })` and `.find()`-ed
 * the slug, so it degraded to the raw slug for every team past that page.
 * See ADR 0009.
 */
export function TeamCrumb({ slug }: { slug: string | undefined }): ReactElement {
  const { data, isError } = useTeamByName(slug);
  const label = resolveCrumbLabel(data, slug, (t) => t.displayName ?? t.name, 'Team');
  return isError ? <span title="Couldn't load the team name">{label}</span> : <>{label}</>;
}
